import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  Breadcrumbs,
  Link as MuiLink,
  Chip,
  Stack,
  Paper,
  IconButton,
  Tooltip,
  Divider,
  CircularProgress,
  Avatar,
  Skeleton,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/RefreshOutlined";
import CallIcon from "@mui/icons-material/Call";
import BusinessIcon from "@mui/icons-material/ApartmentOutlined";
import ConfirmationNumberIcon from "@mui/icons-material/ConfirmationNumberOutlined";
import HourglassTopIcon from "@mui/icons-material/HourglassTopOutlined";
import BuildIcon from "@mui/icons-material/BuildOutlined";
import CheckCircleIcon from "@mui/icons-material/CheckCircleOutlined";
import ArrowForwardIcon from "@mui/icons-material/ArrowForwardRounded";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import api from "../services/api";

// Shared call-shortcut palette so the dashboard's contact rows match the
// look used on Companies / Tickets / Search / Offline pages.
const CONTACT_COLORS = {
  operator:    { idle: "#a78bfa", hover: "#8b5cf6" }, // violet
  electrician: { idle: "#fbbf24", hover: "#f59e0b" }, // amber
  secretary:   { idle: "#34d399", hover: "#10b981" }, // mint/teal
};

const telHref = (phone) => {
  if (!phone) return null;
  const digits = String(phone).replace(/[^\d+]/g, "");
  return digits ? `tel:${digits}` : null;
};

// Same compact 3-line `📞 Label-Name-Number` block used in the Companies
// and Tickets tables. Wraps cleanly when name/number are long.
const ContactStrip = ({ company }) => {
  const c = company || {};
  const rows = [
    { label: "Operator",    name: c.operator_name,     phone: c.operator_phone,           colors: CONTACT_COLORS.operator },
    { label: "Electrician", name: null,                phone: c.local_electrician_number, colors: CONTACT_COLORS.electrician },
    { label: "Secretary",   name: c.secretary_name,    phone: c.secretary_phone,          colors: CONTACT_COLORS.secretary },
  ];
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5, minWidth: 0, mt: 0.75 }}>
      {rows.map((r) => {
        const callable = Boolean(r.phone);
        return (
          <Box
            key={r.label}
            onClick={callable ? (e) => e.stopPropagation() : undefined}
            component={callable ? "a" : "div"}
            href={callable ? telHref(r.phone) : undefined}
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: 0.75,
              borderRadius: "4px",
              py: 0.25,
              minWidth: 0,
              textDecoration: "none",
              color: "inherit",
              ...(callable && {
                cursor: "pointer",
                "&:hover": {
                  backgroundColor: "rgba(0,0,0,0.04)",
                  "& .ds-call-btn": { backgroundColor: r.colors.hover },
                },
              }),
            }}
          >
            <Box
              className="ds-call-btn"
              sx={{
                width: 20,
                height: 20,
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                mt: 0.125,
                backgroundColor: callable ? r.colors.idle : "rgba(0,0,0,0.08)",
                transition: "background-color 0.15s",
                "& .MuiSvgIcon-root": {
                  color: callable ? "#ffffff" : "rgba(0,0,0,0.3)",
                  fontSize: 11,
                },
              }}
            >
              <CallIcon />
            </Box>
            <Typography
              variant="caption"
              sx={{
                fontSize: "0.6875rem",
                lineHeight: 1.35,
                whiteSpace: "normal",
                wordBreak: "break-word",
                minWidth: 0,
                flex: 1,
              }}
            >
              <Box component="span" sx={{ fontWeight: 600, color: "text.secondary" }}>
                {r.label}
              </Box>
              <Box component="span" sx={{ color: r.name ? "text.primary" : "text.disabled", mx: 0.25 }}>
                -{r.name || "—"}
              </Box>
              <Box
                component="span"
                sx={{
                  fontFamily: "monospace",
                  fontWeight: 500,
                  color: r.phone ? "text.primary" : "text.disabled",
                }}
              >
                -{r.phone || "—"}
              </Box>
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
};

const KpiCard = ({ icon, label, value, accent, trend, loading, onClick }) => (
  <Card
    onClick={onClick}
    sx={{
      height: "100%",
      cursor: onClick ? "pointer" : "default",
      transition: "transform .15s ease, box-shadow .15s ease, border-color .15s ease",
      "&:hover": onClick
        ? {
            transform: "translateY(-2px)",
            boxShadow: (t) => t.customShadows?.lg,
            borderColor: "primary.main",
          }
        : undefined,
    }}
  >
    <CardContent sx={{ p: 2 }}>
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" mb={1.25}>
        <Box
          sx={{
            width: 36,
            height: 36,
            borderRadius: "10px",
            backgroundColor: accent?.bg || "primary.light",
            color: accent?.fg || "primary.main",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {icon}
        </Box>
        {trend && (
          <Chip
            label={trend}
            size="small"
            sx={{
              height: 20,
              fontSize: "0.625rem",
              fontWeight: 600,
              backgroundColor: "success.light",
              color: "success.dark",
            }}
          />
        )}
      </Stack>
      <Typography
        variant="caption"
        sx={{
          display: "block",
          color: "text.secondary",
          textTransform: "uppercase",
          letterSpacing: "0.06em",
          fontSize: "0.625rem",
          fontWeight: 700,
          mb: 0.5,
        }}
      >
        {label}
      </Typography>
      {loading ? (
        <Skeleton width={64} height={36} />
      ) : (
        <Typography variant="h2" sx={{ fontWeight: 700, fontSize: "1.75rem", lineHeight: 1.1 }}>
          {value}
        </Typography>
      )}
    </CardContent>
  </Card>
);

const STATUS_LABELS = {
  open: "Open",
  ongoing: "Ongoing",
  waiting_user_feedback: "Waiting · feedback",
  waiting_material: "Waiting · material",
  waiting_service: "Waiting · service",
  resolved: "Resolved",
  removed: "Removed",
};

const STATUS_COLOR = {
  open: { bg: "#fee2e2", fg: "#b91c1c" },
  ongoing: { bg: "#fef3c7", fg: "#b45309" },
  waiting_user_feedback: { bg: "#ede9fe", fg: "#6d28d9" },
  waiting_material: { bg: "#ede9fe", fg: "#6d28d9" },
  waiting_service: { bg: "#ede9fe", fg: "#6d28d9" },
  resolved: { bg: "#d1fae5", fg: "#047857" },
  removed: { bg: "#f1f1f1", fg: "#6a6d70" },
};

const StatusPill = ({ status }) => {
  const c = STATUS_COLOR[status] || STATUS_COLOR.open;
  return (
    <Chip
      label={STATUS_LABELS[status] || status}
      size="small"
      sx={{
        height: 20,
        fontSize: "0.625rem",
        fontWeight: 600,
        backgroundColor: c.bg,
        color: c.fg,
      }}
    />
  );
};

// Recent Companies displays at most 5 rows. We additionally filter out
// companies whose name is 10 chars or shorter — those are typically the
// short numeric codes ("U194") used for internal tracking, not the
// human-named "real" customers operators want to see first.
const RECENT_LIMIT = 5;
const MIN_NAME_LENGTH = 10;
const pickRecentForDashboard = (items = []) =>
  items
    .filter((c) => (c?.name || "").trim().length > MIN_NAME_LENGTH)
    .slice(0, RECENT_LIMIT);

const SectionHeader = ({ title, sub, action }) => (
  <Stack
    direction="row"
    alignItems="center"
    justifyContent="space-between"
    sx={{ px: 2, py: 1.5 }}
  >
    <Box>
      <Typography variant="h5" sx={{ fontWeight: 700 }}>
        {title}
      </Typography>
      {sub && (
        <Typography variant="caption" color="text.secondary">
          {sub}
        </Typography>
      )}
    </Box>
    {action}
  </Stack>
);

export default function DashboardPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    companies: 0,
    tickets: 0,
    open: 0,
    waitingService: 0,
    resolved: 0,
  });
  const [recentTickets, setRecentTickets] = useState([]);
  const [recentCompanies, setRecentCompanies] = useState([]);
  // "live" (default) shows only production-active companies; "all" shows
  // every status for a quick onboarding-overview view.
  const [recentScope, setRecentScope] = useState("live");

  const load = async (scope = recentScope) => {
    setLoading(true);
    try {
      // Fetch more than we display so we have a pool to filter from —
      // the dashboard only shows companies with longer, "real" names
      // (length > 10), skipping the short numeric codes like "U194".
      const recentCompaniesUrl =
        scope === "live"
          ? "/companies?limit=40&sort=-createdAt&status=live"
          : "/companies?limit=40&sort=-createdAt";
      const [c, t, openT, waitingServiceT, resolvedT, recentT, recentC] =
        await Promise.all([
          api.get("/companies?limit=1"),
          api.get("/tickets?limit=1"),
          api.get("/tickets?status=open&limit=1"),
          api.get("/tickets?status=waiting_service&limit=1"),
          api.get("/tickets?status=resolved&limit=1"),
          api.get("/tickets?limit=6"),
          api.get(recentCompaniesUrl),
        ]);
      setStats({
        companies: c.data.data.pagination.total,
        tickets: t.data.data.pagination.total,
        open: openT.data.data.pagination.total,
        waitingService: waitingServiceT.data.data.pagination.total,
        resolved: resolvedT.data.data.pagination.total,
      });
      setRecentTickets(recentT.data.data.items || []);
      setRecentCompanies(pickRecentForDashboard(recentC.data.data.items));
    } catch (e) {
      // surface silently
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-fetch only the Recent Companies list when the scope toggle changes
  // (avoids reloading every other KPI on a small UI tweak).
  useEffect(() => {
    let alive = true;
    const url =
      recentScope === "live"
        ? "/companies?limit=40&sort=-createdAt&status=live"
        : "/companies?limit=40&sort=-createdAt";
    api.get(url).then(({ data }) => {
      if (alive) setRecentCompanies(pickRecentForDashboard(data.data.items));
    });
    return () => {
      alive = false;
    };
  }, [recentScope]);

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
        <Breadcrumbs
          separator="›"
          sx={{ fontSize: "0.9375rem", display: { xs: "none", sm: "flex" } }}
        >
          <Typography color="text.secondary" sx={{ fontSize: "0.9375rem" }}>Home</Typography>
          <Typography color="text.primary" sx={{ fontSize: "0.9375rem", fontWeight: 600 }}>
            Dashboard
          </Typography>
        </Breadcrumbs>
        <Box sx={{ display: { xs: "none", md: "inline-flex" } }}>
          <Tooltip title="Refresh">
            <IconButton
              onClick={load}
              sx={{
                border: "1px solid",
                borderColor: "divider",
                backgroundColor: "background.paper",
              }}
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Stack>

      <Grid container spacing={2} mb={2.5}>
        <Grid item xs={6} sm={6} md={3}>
          <KpiCard
            icon={<BusinessIcon sx={{ fontSize: 18 }} />}
            label="Companies"
            value={stats.companies}
            accent={{ bg: "#ccfbf1", fg: "#0f766e" }}
            loading={loading}
            onClick={() => navigate("/companies")}
          />
        </Grid>
        <Grid item xs={6} sm={6} md={3}>
          <KpiCard
            icon={<HourglassTopIcon sx={{ fontSize: 18 }} />}
            label="Open Tickets"
            value={stats.open}
            accent={{ bg: "#fee2e2", fg: "#b91c1c" }}
            loading={loading}
            onClick={() => navigate("/tickets?status=open")}
          />
        </Grid>
        <Grid item xs={6} sm={6} md={3}>
          <KpiCard
            icon={<BuildIcon sx={{ fontSize: 18 }} />}
            label="Waiting Service"
            value={stats.waitingService}
            accent={{ bg: "#ede9fe", fg: "#6d28d9" }}
            loading={loading}
            onClick={() => navigate("/tickets?status=waiting_service")}
          />
        </Grid>
        <Grid item xs={6} sm={6} md={3}>
          <KpiCard
            icon={<CheckCircleIcon sx={{ fontSize: 18 }} />}
            label="Resolved Tickets"
            value={stats.resolved}
            accent={{ bg: "#d1fae5", fg: "#047857" }}
            loading={loading}
            onClick={() => navigate("/tickets?status=resolved")}
          />
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={12} lg={7}>
          <Paper variant="outlined">
            <SectionHeader
              title="Recent Tickets"
              sub={`${stats.tickets} total · latest first`}
              action={
                <MuiLink
                  component="button"
                  underline="hover"
                  onClick={() => navigate("/tickets")}
                  sx={{
                    fontSize: "0.75rem",
                    fontWeight: 600,
                    color: "primary.main",
                    display: "flex",
                    alignItems: "center",
                    gap: 0.25,
                  }}
                >
                  View all <ArrowForwardIcon sx={{ fontSize: 14 }} />
                </MuiLink>
              }
            />
            <Divider />
            {loading && recentTickets.length === 0 && (
              <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <CircularProgress size={20} />
              </Box>
            )}
            {!loading && recentTickets.length === 0 && (
              <Box sx={{ textAlign: "center", py: 4, color: "text.secondary" }}>
                <ConfirmationNumberIcon sx={{ fontSize: 32, opacity: 0.4, mb: 0.5 }} />
                <Typography variant="caption" display="block">
                  No tickets yet.
                </Typography>
              </Box>
            )}
            <Stack divider={<Divider />}>
              {recentTickets.map((t) => (
                <Box
                  key={t._id}
                  sx={{
                    px: 2,
                    py: 1.5,
                    cursor: "pointer",
                    transition: "background-color .12s ease",
                    "&:hover": { backgroundColor: "action.hover" },
                  }}
                  onClick={() => navigate(`/tickets?selected=${t._id}`)}
                >
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={0.5} spacing={1}>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 600, fontSize: "0.8125rem", mb: 0.25 }}
                      >
                        {t.heading}
                      </Typography>
                      <Stack direction="row" spacing={1.25} alignItems="center" flexWrap="wrap">
                        <Typography variant="caption" sx={{ fontFamily: "monospace", color: "text.secondary" }}>
                          {t.imei}
                        </Typography>
                        {t.company?.name && (
                          <>
                            <Typography variant="caption" color="text.disabled">·</Typography>
                            <Typography variant="caption" color="text.secondary">
                              {t.company.name}
                            </Typography>
                          </>
                        )}
                      </Stack>
                    </Box>
                    <Stack alignItems="flex-end" spacing={0.5} sx={{ flexShrink: 0 }}>
                      <StatusPill status={t.status} />
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.6875rem" }}>
                        {dayjs(t.updatedAt).format("DD MMM · HH:mm")}
                      </Typography>
                    </Stack>
                  </Stack>
                  <ContactStrip company={t.company} />
                </Box>
              ))}
            </Stack>
          </Paper>
        </Grid>

        <Grid item xs={12} lg={5}>
          <Paper variant="outlined">
            <SectionHeader
              title="Recent Companies"
              sub={`Latest ${recentScope === "live" ? "live" : "any-status"} · names longer than ${MIN_NAME_LENGTH} chars`}
              action={
                <Stack direction="row" alignItems="center" spacing={0.75}>
                  <Stack
                    direction="row"
                    sx={{
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: "4px",
                      overflow: "hidden",
                    }}
                  >
                    {["live", "all"].map((opt) => {
                      const active = recentScope === opt;
                      return (
                        <Box
                          key={opt}
                          onClick={() => setRecentScope(opt)}
                          sx={{
                            px: 1,
                            py: 0.25,
                            cursor: "pointer",
                            fontSize: "0.6875rem",
                            fontWeight: 600,
                            textTransform: "capitalize",
                            color: active ? "common.white" : "text.secondary",
                            backgroundColor: active ? "primary.main" : "transparent",
                            transition: "background-color .12s ease",
                            "&:hover": {
                              backgroundColor: active ? "primary.dark" : "action.hover",
                            },
                          }}
                        >
                          {opt}
                        </Box>
                      );
                    })}
                  </Stack>
                  <MuiLink
                    component="button"
                    underline="hover"
                    onClick={() => navigate("/companies")}
                    sx={{
                      fontSize: "0.75rem",
                      fontWeight: 600,
                      color: "primary.main",
                      display: "flex",
                      alignItems: "center",
                      gap: 0.25,
                    }}
                  >
                    View all <ArrowForwardIcon sx={{ fontSize: 14 }} />
                  </MuiLink>
                </Stack>
              }
            />
            <Divider />
            {loading && recentCompanies.length === 0 && (
              <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
                <CircularProgress size={20} />
              </Box>
            )}
            <Stack divider={<Divider />}>
              {recentCompanies.map((c) => (
                <Box
                  key={c._id}
                  onClick={() => navigate(`/companies/${c._id}/details`)}
                  sx={{
                    px: 2,
                    py: 1.25,
                    cursor: "pointer",
                    transition: "background-color .12s ease",
                    "&:hover": { backgroundColor: "action.hover" },
                  }}
                >
                  <Stack direction="row" alignItems="center" spacing={1.5}>
                    <Avatar
                      sx={{
                        width: 34,
                        height: 34,
                        backgroundColor: "primary.light",
                        color: "primary.main",
                        fontSize: "0.8125rem",
                        fontWeight: 700,
                      }}
                    >
                      {(c.name || "?").charAt(0).toUpperCase()}
                    </Avatar>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, fontSize: "0.8125rem" }}>
                        {c.name}
                      </Typography>
                      <Stack direction="row" spacing={1} alignItems="center">
                        {c.manufacturing_id != null && (
                          <Typography variant="caption" sx={{ fontFamily: "monospace", color: "primary.main", fontWeight: 600 }}>
                            #{c.manufacturing_id}
                          </Typography>
                        )}
                        <Typography variant="caption" color="text.secondary">
                          {c.district || c.block || c.panchayat || "—"}
                        </Typography>
                      </Stack>
                    </Box>
                    <Chip
                      label={c.status || "—"}
                      size="small"
                      sx={{
                        height: 18,
                        fontSize: "0.625rem",
                        fontWeight: 600,
                        backgroundColor: "#eaf0ff",
                        color: "#0f766e",
                      }}
                    />
                  </Stack>
                  <ContactStrip company={c} />
                </Box>
              ))}
            </Stack>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}
